import { useState } from 'react'
import useAuthHook from '../userAuth'
import { Link, useNavigate } from 'react-router-dom'

import logo from '../assets/logo.png'

const NavLink = ({ to, title }) => {
  return (
    <li>
      <Link to={to}>
        <div className='text-blue-1 transition hover:text-blue-7/75 hover:underline underline-offset-2 active:underline'>
          {title}
        </div>
      </Link>
    </li>
  )
}

/**
 * Returns the navigation links for a given role.
 *
 * The "Support" link is a menu-visibility gate only (Requirement 9): admins
 * always see it, and customers see it only when their resolved customer record
 * has `supportTicketsEnabled === true` (default hidden). This flag is not a
 * security boundary — Firestore/Storage rules and Cloud Functions never consult it.
 *
 * @param {string} role
 * @param {{ supportTicketsEnabled?: boolean }} [options]
 */
export const getNavLinksForRole = (role, { supportTicketsEnabled } = {}) => {
  if (role === 'admin') {
    return [
      { to: '/tokens', title: 'Tokens' },
      { to: '/customers', title: 'Customers' },
      { to: '/api-usage', title: 'API Usage' },
      { to: '/api', title: 'Medipro Browser' },
      { to: '/activity-log', title: 'Activity Log' },
      { to: '/settings', title: 'Settings' },
      { to: '/tickets', title: 'Support' }
    ]
  }
  if (role === 'customer') {
    const links = [
      { to: '/my-keys', title: 'My Keys' },
      { to: '/api-usage', title: 'API Usage' },
      { to: '/api', title: 'Medipro Browser' }
    ]
    if (supportTicketsEnabled === true) {
      links.push({ to: '/tickets', title: 'Support' })
    }
    return links
  }
  return []
}

const Navbar = ({ role, supportTicketsEnabled }) => {
  const { user, logout } = useAuthHook()
  const navigate = useNavigate()

  const [dropDown, setDropDown] = useState(false)

  const dropDownFlip = (e) => {
    e.preventDefault()
    setDropDown(!dropDown)
  }

  const closeDropDown = () => {
    setDropDown(false)
  }

  const navLinks = getNavLinksForRole(role, { supportTicketsEnabled })

  return (
    <header aria-label='Site Header' className='bg-blue-10'>
      <div className='mx-auto max-w-screen px-4 sm:px-6 lg:px-8'>
        <div className='flex  h-16 items-center justify-between'>
          <div className='flex-1 md:flex md:items-center md:gap-12'>
            <Link to='/'>
              <div
                className='text-blue-4 flex flex-row'
                onClick={closeDropDown}
              >
                <span className='sr-only'>Home</span>
                <img alt='logo' className='h-8' src={logo} />
                <p className='ml-2 mt-1'>Inspire Health Tech Self-Service</p>
              </div>
            </Link>
          </div>
          <div className='md:flex md:items-center md:gap-12'>
            <nav aria-label='Site Nav' className='hidden md:block' data-tour='nav-links'>
              <ul className='flex items-center gap-6 text-base'>
                {user && navLinks.map((link) => (
                  <NavLink key={link.to} to={link.to} title={link.title} />
                ))}
              </ul>
            </nav>
            <div className='flex items-center gap-4'>
              <div className='sm:flex sm:gap-4 hidden md:visable'>
                {user && (
                  <button
                    className='rounded bg-blue-6 hover:bg-blue-7 p-2 text-blue-1 transition'
                    onClick={() => {
                      logout()
                      navigate('/')
                    }}
                  >
                    Logout
                  </button>
                )}
                {!user && (
                  <button
                    className='rounded bg-blue-6 hover:bg-blue-7 p-2 text-blue-1 transition'
                    onClick={() => navigate('/login')}
                  >
                    Sign In
                  </button>
                )}
              </div>

              <div className='block md:hidden'>
                <button
                  onClick={dropDownFlip}
                  className='rounded bg-white  p-2 text-blue-10 transition hover:text-blue-8'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-5 w-5'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                    strokeWidth='2'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M4 6h16M4 12h16M4 18h16'
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {dropDown && (
        <div
          className='flex flex-col items-center absolute bg-blue-10 mx-auto w-screen px-4 sm:px-6 lg:px-8 py-2 sm:hidden border-t-2 border-teal-600'
          onClick={dropDownFlip}
        >
          {user && (
            <nav aria-label='Site Nav' className='text-gray-500'>
              <ul>
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className='text-blue-1 transition hover:text-blue-7/75'>
                      {link.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          {user && (
            <button
              className='my-4 rounded bg-blue-4 hover:bg-blue-7 p-2 text-blue-9 transition hover:text-blue-1'
              onClick={() => {
                logout()
                navigate('/')
              }}
            >
              Logout
            </button>
          )}
          {!user && (
            <button
              className='my-4 rounded bg-blue-4 hover:bg-blue-7 p-2 text-blue-9 transition hover:text-blue-1'
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
          )}
        </div>
      )}
    </header>
  )
}
export default Navbar
